import { createFeatureSelector, createSelector } from '@ngrx/store';

import { groupArrayByKey } from '@campus/utils';
import { Dictionary } from '@ngrx/entity';
import { NewsCategoryEnum, NewsInterface } from '../../+models';
import { MethodQueries } from '../method';
import { MethodNewsQueries } from '../method-news';
import { NewsCategoryQueries } from '../news-category';
import { NewsCategoryNewsQueries } from '../news-category-news';
import { NAME, selectAll, selectEntities, State } from './news.reducer';

export const selectNewsState = createFeatureSelector<State>(NAME);

export const getError = createSelector(selectNewsState, (state: State) => state.error);

export const getLoaded = createSelector(selectNewsState, (state: State) => state.loaded);

export const getAll = createSelector(selectNewsState, selectAll);

export const getAllEntities = createSelector(selectNewsState, selectEntities);

export const getNewsWithMethodsAndCategories = createSelector(
  getAll,
  MethodQueries.getAllEntities,
  MethodNewsQueries.getAll,
  NewsCategoryNewsQueries.getByNewsId,
  NewsCategoryQueries.getAllEntities,
  (allNews, methodEntities, allMethodNews, nCnByNewsId, newsCategoryDict): NewsInterface[] => {
    const methodNewsByNewsId = groupArrayByKey(allMethodNews, 'newsId');

    return allNews.map((news: NewsInterface) => {
      const methods = (methodNewsByNewsId[news.id] || []).map((method) => methodEntities[method.methodId]);
      const categories = (nCnByNewsId[news.id] || []).map((nCN) => newsCategoryDict[nCN.newsCategoryId]);
      return { ...news, methods, categories };
    });
  }
);

// The news side sheet only shows three tabs: 'Probleem van de week', '@WouwActua' and 'Tips'.
// News is grouped under every tab category it belongs to (once per tab), news that belongs to none of
// them falls back to 'Tips'.
const NEWS_TAB_CATEGORY_LABELS: string[] = [
  NewsCategoryEnum.PROBLEEM_VAN_DE_WEEK,
  NewsCategoryEnum.WOUW_ACTUA,
  NewsCategoryEnum.TIPS,
];

export const getNewsWithMethodsByNewsTabId = createSelector(
  getNewsWithMethodsAndCategories,
  NewsCategoryQueries.getAll,
  (newsWithMethods, newsCategories): Dictionary<NewsInterface[]> => {
    const tipsCategoryId = newsCategories.find((category) => category.label === NewsCategoryEnum.TIPS)?.id;
    const newsTabCategoryIds = NEWS_TAB_CATEGORY_LABELS.map(
      (label) => newsCategories.find((category) => category.label === label)?.id
    ).filter((categoryId) => categoryId != null);

    return newsWithMethods.reduce((acc: Dictionary<NewsInterface[]>, news: NewsInterface) => {
      const ownCategoryIds = new Set((news.categories ?? []).map((category) => category?.id));
      const newsTabIds = newsTabCategoryIds.filter((categoryId) => ownCategoryIds.has(categoryId));

      if (!newsTabIds.length && tipsCategoryId != null) newsTabIds.push(tipsCategoryId);

      newsTabIds.forEach((newsTabId) => {
        acc[newsTabId] = [...(acc[newsTabId] || []), news];
      });

      return acc;
    }, {});
  }
);

export const getById = createSelector(
  selectNewsState,
  (state: State, props: { id: number }) => state.entities[props.id]
);
